'use client';
import React, { useEffect, useState } from 'react';
import { useTranslation } from '@/hook';

import {
    BookingTypes,
    ReserveTypes,
    RetailBookingStatus,
    CustomerBookingWIthSellingOpp,
    CardStatusTypes,
} from '@/types';
import { numberWithCommas } from '@/utils';
import BookingCard from './booking-card.component';
const BookingCardWrapper = ({
    data,
    cantBeClickedByDefault,
}: {
    data: CustomerBookingWIthSellingOpp;
    cantBeClickedByDefault: null | true;
}) => {
    const {
        bookingDate,
        bookingType,
        reserveType,
        timeslot,
        showroom,
        clientAddress,
        vehicle,
    } = data;
    const {
        id,
        make,
        model,
        year,
        images,
        transmission,
        sylndrSellingPrice,
        kilometers,
        discountPrice,
    } = vehicle;
    const vehicleImage = images[0] || '';
    const { t, i18n, IS_RTL } = useTranslation();

    const [isCardClickable, setIsCardClickable] = useState(false);
    const [showAddPaymentButton, setShowAddPaymentButton] = useState(false);
    const [showControllers, setShowControllers] = useState(true);
    const [cardStatus, setCardStatus] = useState<CardStatusTypes>(
        CardStatusTypes.CANCELED
    );
    const getFormattedDate = (
        date: string | Date | null,
        options?: Intl.DateTimeFormatOptions
    ): string => {
        if (!date) return '--/ --';
        return new Date(date).toLocaleDateString(
            IS_RTL ? 'ar-EG' : 'en-US',
            options
        );
    };
    const getFormattedTime = (
        date: string | Date | null,
        options?: Intl.DateTimeFormatOptions
    ): string => {
        if (!date) return '--/ --';
        return new Date(date).toLocaleTimeString(
            IS_RTL ? 'ar-EG' : 'en-US',
            options
        );
    };

    const getBookingDate = (date: string) => {
        const dateDay = getFormattedDate(date, {
            day: 'numeric',
        });
        const dateMonth = getFormattedDate(date, {
            month: 'short',
        });
        const weekday = getFormattedDate(date, {
            weekday: 'long',
        });
        const time = getFormattedTime(date, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
        const manuallyFormattedDate = `${weekday}, ${dateDay} ${dateMonth} ${
            IS_RTL ? 'الساعة' : 'at'
        } ${time}`;

        return manuallyFormattedDate;
    };

    const customerBookingDate = !bookingDate
        ? '--/ --'
        : getBookingDate(`${bookingDate?.substring(0, 10)} ${timeslot.from}`);

    const vehicleName = `${make} ${model} ${year}`;

    const getReservationTitle = (
        currentReservationType: string,
        bookingType: string
    ) => {
        if (bookingType == BookingTypes.SEE && !currentReservationType) {
            return t('SylndrHubBooking');
        }
        if (currentReservationType == ReserveTypes.HOME) {
            return t('HomeVisit');
        }
        if (currentReservationType == ReserveTypes.SHOWROOM) {
            return t('SylndrHubBooking');
        }
        return '';
    };
    const getShowroomLocation = () => {
        let output = '';
        if (
            showroom &&
            showroom.instructions &&
            showroom.instructions[i18n.language]
        ) {
            output = showroom.instructions[i18n.language];
        }
        return output;
    };
    const constructClientAddress = addressObject => {
        let stringOutput = '';
        const delimiter = i18n.language == 'en' ? ',' : '،';
        if (addressObject) {
            if (addressObject.building && addressObject.streetName)
                stringOutput += `${addressObject.building} ${addressObject.streetName} ${delimiter} `;
            if (addressObject.area)
                stringOutput += `${addressObject.area} ${delimiter} `;
            if (addressObject.city) stringOutput += `${addressObject.city}`;
        }
        return stringOutput;
    };

    const reservationTitle = getReservationTitle(reserveType, bookingType);

    const vehicleKm = numberWithCommas(kilometers);
    const carDetailsLink = `/${i18n.language}/car-details/${id}`;
    const vehiclePrice =
        discountPrice === 0
            ? numberWithCommas(sylndrSellingPrice)
            : numberWithCommas(discountPrice);

    const bookingLocation =
        reservationTitle == t('SylndrHubBooking')
            ? getShowroomLocation()
            : constructClientAddress(clientAddress);

    useEffect(() => {
        const { sellingOpportunity, bookingStatus } = data;
        if (sellingOpportunity) {
            const { sellingOpportunityStatus } = sellingOpportunity;
            setCardStatus(sellingOpportunityStatus);
            setIsCardClickable(true);
            sellingOpportunityStatus === CardStatusTypes.PURCHASING &&
                setShowAddPaymentButton(true);
            sellingOpportunityStatus !== CardStatusTypes.PURCHASING &&
                setShowControllers(false);
        } else {
            switch (bookingStatus) {
                case RetailBookingStatus.Canceled:
                case RetailBookingStatus.ClosedFailure:
                case RetailBookingStatus.NoShow:
                    setCardStatus(CardStatusTypes.CANCELED);
                    setShowControllers(false);
                    break;
                case RetailBookingStatus.Rescheduled:
                    setCardStatus(CardStatusTypes.RESCHEDULED);
                    break;
                case RetailBookingStatus.Scheduled:
                case RetailBookingStatus.Show:
                    setCardStatus(CardStatusTypes.SCHEDULED);
                    break;
                case RetailBookingStatus.ClosedSuccess:
                    setCardStatus(CardStatusTypes.SUCCESSFUL);
                    setShowControllers(false);
                    break;
                default:
                    break;
            }
        }
    }, []);
    return (
        <BookingCard
            vehicleName={vehicleName}
            vehicleImage={vehicleImage}
            vehicleTransmission={t(transmission)}
            vehicleKm={vehicleKm}
            carDetailsLink={carDetailsLink}
            bookingDate={customerBookingDate}
            reservationTitle={reservationTitle}
            vehiclePrice={vehiclePrice}
            bookingLocation={bookingLocation}
            cardStatus={cardStatus}
            reserveType={reserveType}
            bookingType={bookingType}
            cardIsClickable={isCardClickable}
            showAddPaymentButton={showAddPaymentButton}
            showControllers={showControllers}
            id={data.id}
            cantBeClickedByDefault={cantBeClickedByDefault}
        />
    );
};

export default BookingCardWrapper;
